import { DropdownQueryParam } from 'clerk_common/constants/urls/queryParams';
import _ from 'lodash';
import {
  parseAsArrayOf,
  parseAsString,
  SingleParserBuilder,
  useQueryStates,
} from 'nuqs';

type UseFiltersState = {
  // add state here
};

export interface HandleUpdateidSelectionProps {
  queryParam: DropdownQueryParam;
  id: string;
  checked: boolean;
}

export interface HandleSelectManyProps {
  queryParam: DropdownQueryParam;
  ids: string[];
}

type UseFiltersActions = {
  /** Handles updating the query param array */
  handleUpdateIdSelection: (
    props: HandleUpdateidSelectionProps
  ) => Promise<void>;
  /** Clear current selection and select many */
  handleSelectMany: (props: HandleSelectManyProps) => Promise<void>;
  /** Checks if the value is selected or not */
  checkIfValueIsSelected: (
    value: string,
    type: DropdownQueryParam
  ) => boolean | undefined;
  /** Gets the min/max value from the query param. */
  getMinMaxAmount: (type: DropdownQueryParam) => string | null;
  /** Handles updating the query param array for min/max values. */
  setMinMaxAmount: ({
    minAmount,
    maxAmount,
    checked,
    minParam,
    maxParam,
    optionsToClear,
  }: {
    minAmount: string;
    maxAmount: string;
    checked: boolean;
    minParam: DropdownQueryParam;
    maxParam: DropdownQueryParam;
    /**
     * Use to remove other filters when replacing with a new value.
     * Use instead of calling `clearFilter`/`clearFilters` as
     * there is a delay and the clear will not end up updating.
     */
    optionsToClear?: string[];
  }) => Promise<void>;
  /** Function to clear all selected filters from one query param. */
  clearFilter: (queryParam: DropdownQueryParam) => Promise<void>;
  clearFilters: (queryParams: DropdownQueryParam[]) => Promise<void>;
  clearAllFilters: () => void;
};

export type UseFiltersValue = UseFiltersState & UseFiltersActions;

/**
 * This hook is used to handle filters
 * The available filters are defined in the DropdownQueryParam enum
 */
const useFilters = (): UseFiltersValue => {
  const params = Object.values(DropdownQueryParam);
  const possibleParams = params.reduce(
    (acc, curr) => ({ ...acc, [curr]: parseAsArrayOf(parseAsString) }),
    {}
  ) as Record<DropdownQueryParam, SingleParserBuilder<string[]>>;

  const [dropdownParams, setDropdownParams] = useQueryStates(possibleParams);

  /**
   * This function handles the selection of a dropdown item.
   * It then updates the dropdownParams to include the selected entity in the query param.
   * @param {Object} options
   * @param {string} options.id - The id of the entity that was selected
   * @param {boolean} options.checked - Whether or not the entity was checked
   * @param {DropdownQueryParam} options.queryParam - The query param that the entity should be added to
   */
  const handleUpdateIdSelection = async ({
    id,
    checked,
    queryParam,
  }: HandleUpdateidSelectionProps) => {
    if (Object.values(DropdownQueryParam).includes(queryParam) === false) {
      throw new Error('Invalid query param');
    }
    await setDropdownParams(
      {
        ...dropdownParams,
        [queryParam]: checked
          ? [...(dropdownParams[queryParam] ?? []), id]
          : dropdownParams[queryParam]?.filter((entityId) => entityId !== id),
      },
      { shallow: true }
    );
  };

  /**
   * This function handles the selection of many dropdown items.
   * It then updates the dropdownParams to include ONLY the selected entities in the query param.
   * @param {string[]} options.ids - The ids of the entity that should be exclusively selected
   * @param {DropdownQueryParam} options.queryParam - The query param that the entity should be added to*/
  const handleSelectMany = async ({
    ids,
    queryParam,
  }: HandleSelectManyProps) => {
    if (Object.values(DropdownQueryParam).includes(queryParam) === false) {
      throw new Error('Invalid query param');
    }
    await setDropdownParams(
      {
        ...dropdownParams,
        [queryParam]: ids,
      },
      { shallow: true }
    );
    return;
  };

  const checkIfValueIsSelected = (
    value: string,
    type: DropdownQueryParam
  ): boolean | undefined => {
    return dropdownParams[type]?.includes(value);
  };

  const getMinMaxAmount = (queryParam: DropdownQueryParam): string | null => {
    return dropdownParams[queryParam]?.[0] ?? null;
  };

  /** Only use for the custom min/max amount filters. Requires two query params. */
  const setMinMaxAmount = async ({
    minAmount,
    maxAmount,
    checked,
    minParam,
    maxParam,
    optionsToClear,
  }: {
    minAmount: string;
    maxAmount: string;
    checked: boolean;
    minParam: DropdownQueryParam;
    maxParam: DropdownQueryParam;
    optionsToClear?: string[];
  }) => {
    const emptyQueryParams = (
      dropdownParams: { [key: string]: string[] | null },
      queryParamsToEmpty: string[]
    ) => {
      Object.keys(dropdownParams).forEach((key) => {
        if (queryParamsToEmpty.includes(key)) {
          dropdownParams[key] = [];
        }
      });
      return dropdownParams;
    };
    const newDropdownParams = emptyQueryParams(
      { ...dropdownParams },
      optionsToClear ?? []
    );
    if (checked) {
      await setDropdownParams(
        {
          ...newDropdownParams,
          [minParam]: [minAmount],
          [maxParam]: [maxAmount],
        },
        {
          shallow: true,
        }
      );
    } else {
      await setDropdownParams(
        {
          ...dropdownParams,
          [minParam]: [],
          [maxParam]: [],
        },
        {
          shallow: true,
        }
      );
    }
  };

  const clearFilters = async (queryParams: DropdownQueryParam[]) => {
    const emptyQueryParams = (
      dropdownParams: { [key: string]: string[] | null },
      queryParamsToEmpty: string[]
    ) => {
      Object.keys(dropdownParams).forEach((key) => {
        if (queryParamsToEmpty.includes(key)) {
          dropdownParams[key] = [];
        }
      });
      return dropdownParams;
    };
    const newDropdownParams = emptyQueryParams(
      { ...dropdownParams },
      queryParams
    );
    await setDropdownParams(
      {
        ...newDropdownParams,
      },
      {
        shallow: true,
      }
    );
  };

  const clearFilter = async (queryParam: DropdownQueryParam) => {
    await setDropdownParams(
      {
        ...dropdownParams,
        [queryParam]: [],
      },
      {
        shallow: true,
      }
    );
  };

  const clearAllFilters = () => {
    setDropdownParams({
      ..._.mapValues(dropdownParams, () => null),
    });
  };

  return {
    handleUpdateIdSelection,
    handleSelectMany,
    checkIfValueIsSelected,
    getMinMaxAmount,
    setMinMaxAmount,
    clearFilter,
    clearFilters,
    clearAllFilters,
  };
};

export default useFilters;
